import FilterDisplayChip from '@app/src/components/FilterDisplayChip'
import type { AnswerFlagFormData } from '@app/src/pages/Configurations/ConfigurationsPages/AnswerFlag/CreateAnswerFlagModal'
import { AnswerRuleType } from '@app/src/types/resourceExplorer'
import { getRiskColor, getRiskStatusTranslationKey, getRiskTextContrastColor } from '@app/src/utils/riskStatusHelpers'
import { Box, Chip, Stack, Typography } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

const getIntlKeyForRuleType = (ruleType: AnswerRuleType) => {
  switch (ruleType) {
    case AnswerRuleType.NumberGreaterThan:
      return 'answerFlagRules.creation.summary.aboveNumber'
    case AnswerRuleType.NumberLowerThan:
      return 'answerFlagRules.creation.summary.belowNumber'
    case AnswerRuleType.NumberEqualTo:
      return 'answerFlagRules.creation.summary.equalNumber'
    default:
      return undefined
  }
}

const getTriggerValueDisplay = (
  formatMessage: IntlShape['formatMessage'],
  ruleType?: AnswerRuleType,
  ruleValue?: string | string[],
  question?: AnswerFlagFormData['question'],
): string => {
  if (!ruleValue || !ruleType) return ''
  if (!Array.isArray(ruleValue)) {
    const intlKey = getIntlKeyForRuleType(ruleType)
    return intlKey ? formatMessage({ id: intlKey }, { number: ruleValue }) : ruleValue
  }

  switch (ruleType) {
    case AnswerRuleType.SelectedValue:
      return ruleValue
        .map(v => question?.requestCollection?.items.find(item => item.value === v)?.displayText)
        .filter(Boolean)
        .join(', ')
    case AnswerRuleType.AnswerStatus:
      return ruleValue
        .map(value =>
          formatMessage({
            id: `templateBuilder.answerClassifications.classificationsValues.${value}`,
          }),
        )
        .join(', ')
    default:
      return ''
  }
}

const AnswerFlagSummary: React.FC = () => {
  const { formatMessage } = useIntl()
  const { getValues } = useFormContext<AnswerFlagFormData>()

  const { question, ruleType, ruleValue, providerFilters, riskStatus, description } = getValues()

  const triggerValueDisplay = getTriggerValueDisplay(formatMessage, ruleType, ruleValue, question)
  const hasFilters = providerFilters && providerFilters.length > 0

  return (
    <Stack spacing={3}>
      <SummarySection label={formatMessage({ id: 'answerFlagRules.creation.summary.trigger' })}>
        <Typography variant="body1">{question?.questionText}</Typography>
        <Typography variant="body2" color="text.secondary">
          {triggerValueDisplay}
        </Typography>
      </SummarySection>

      <SummarySection label={formatMessage({ id: 'answerFlagRules.creation.summary.segment' })}>
        {hasFilters ? (
          <Box display="flex" gap={1} flexWrap="wrap">
            {providerFilters?.map(filter =>
              filter.filters.map(f => (
                <FilterDisplayChip
                  key={`${filter.name}-${f.value}-${f.operator}`}
                  filterName={filter.name}
                  filterValue={f.value}
                  operator={f.operator}
                />
              )),
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {formatMessage({ id: 'answerFlagRules.creation.summary.allOrganizations' })}
          </Typography>
        )}
      </SummarySection>

      <SummarySection label={formatMessage({ id: 'answerFlagRules.creation.summary.severity' })}>
        {riskStatus && (
          <Chip
            label={formatMessage({ id: getRiskStatusTranslationKey(riskStatus) })}
            size="small"
            sx={{
              backgroundColor: getRiskColor(riskStatus),
              color: getRiskTextContrastColor(riskStatus),
              fontWeight: 500,
            }}
          />
        )}
      </SummarySection>

      <SummarySection label={formatMessage({ id: 'answerFlagRules.creation.summary.titleLabel' })}>
        <Typography variant="body1">{description}</Typography>
      </SummarySection>
    </Stack>
  )
}

const SummarySection: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="subtitle1" gutterBottom>
      {label}
    </Typography>
    <Box sx={{ backgroundColor: 'grey.100', borderRadius: 1, p: 2 }}>{children}</Box>
  </Box>
)

export default AnswerFlagSummary
